import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form as FormikForm } from 'formik';
import { Button, Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useAdminUpdateClientMutation, AdminUpdateClientInput } from 'api';
import { useProtectedClient, useNotify } from 'hooks';
import { CustomSVG, PaddedPaper, ValidatedTextField } from 'components';
import { AdminUpdateClient } from 'features/admin/validations';
import { useCreateContentDomain } from 'features/admin/updaters';

type Values = AdminUpdateClientInput;

const useStyles = makeStyles(theme => ({
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  help: {
    fontWeight: theme.typography.fontWeightRegular,
    marginBottom: theme.spacing(3),
  },
  action: {
    paddingBottom: theme.spacing(2),
  },
}));

const CreateContentDomain: FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const notify = useNotify();
  const client = useProtectedClient();
  const mutationOptions = useCreateContentDomain();
  const { mutate, isLoading } = useAdminUpdateClientMutation({
    onSuccess: editNotify,
    ...mutationOptions,
  });

  const initialValues: AdminUpdateClientInput = {
    id: client.id,
    content_domains: [''].concat(client.content_domains),
  };

  function editNotify() {
    const message = t('components:savedSuccessfully');
    notify.info({ message });
  }

  function handleSubmit(values: Values) {
    mutate({ data: values });
  }

  function renderForm() {
    return (
      <FormikForm>
        <PaddedPaper>
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              <CustomSVG name="track" color="primary" size={32} />
            </Grid>
            <Grid item>
              <Typography variant="h4">{t('parameters:urlTracking')}</Typography>
            </Grid>
          </Grid>
          <Divider className={classes.divider} />
          <Typography className={classes.help} variant="subtitle2" paragraph>
            {t('parameters:trackingHelp')}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={5}>
              <ValidatedTextField
                name="content_domains[0]"
                i18nKey="admin:newDomain"
                color="primary"
                TextFieldProps={{ margin: 'none', label: null }}
              />
            </Grid>
            <Grid item xs={2}>
              <Button
                disabled={isLoading}
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                {t('common:add')}
              </Button>
            </Grid>
          </Grid>
        </PaddedPaper>
      </FormikForm>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={AdminUpdateClient}
    >
      {renderForm()}
    </Formik>
  );
};

export default CreateContentDomain;
